import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useI18n from '@/hooks/useI18n';

interface MenuItem {
  label: string;
  action: () => void;
}

interface WebViewMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin?: () => void;
  onCreateAccount?: () => void;
}

export function WebViewMenu({ isOpen, onClose, onLogin, onCreateAccount }: WebViewMenuProps) {
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  }, [onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  const menuItems: MenuItem[] = [
    { label: t("navigation:login"), action: () => { if (onLogin) onLogin(); } },
    { label: t("navigation:faq"), action: () => navigate('/faq') },
    { label: t("navigation:createAccount"), action: () => { if (onCreateAccount) onCreateAccount(); else navigate('/register'); } },
  ];

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end justify-center transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleBackdropClick}
      data-testid="webview-menu-backdrop"
    >
      <div className="absolute inset-0 bg-[rgba(14,13,13,0.6)]" onClick={handleClose} />

      <div
        className={`relative w-full max-w-[480px] bg-[#f9f9f9] rounded-t-xl shadow-lg transform transition-transform duration-300 ease-out ${
          isClosing ? 'translate-y-full' : 'translate-y-0'
        }`}
        style={{ maxHeight: '70vh' }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-11 h-1 bg-[#d3d5da] rounded-full" />
        </div>

        <div className="px-4 pb-6 flex flex-col">
          {menuItems.map((item, index) => (
            <div key={index} className="w-full">
              <button
                type="button"
                onClick={() => {
                  item.action();
                  handleClose();
                }}
                className="w-full text-left py-4 px-2 text-base text-black font-normal tracking-[-0.4px] hover:bg-gray-100 rounded-lg transition-colors"
                style={{ fontFamily: 'Ubuntu, sans-serif', lineHeight: '20.8px' }}
              >
                {item.label}
              </button>
              {index < menuItems.length - 1 && (
                <div className="w-full h-px bg-[#eeeeee]" />
              )}
            </div>
          ))}

          <div className="w-full h-px bg-[#eeeeee] my-2" />
        </div>
      </div>
    </div>
  );
}

export default WebViewMenu;
